/*
 * bm-dynamatic-shoplift-offer-price.js
 *
 * Local-only display correction for the second-Capo assigned price. It reads
 * the validated offer context from window.__bmDynamaticShopliftBridge
 * (getOfferPrices) and rewrites ONLY the strike/current price text inside the
 * matching widget 7728 card, leaving every other price, total, form, request,
 * style and app config untouched. It installs once behind its own guard.
 */
(function () {
  "use strict";

  if (window.__bmDynamaticShopliftOfferPrice) return;

  var ROOT_SELECTOR = ".dyn-advance-free-gift-container-7728";
  var CARD_SELECTOR = ".advance-free-gift-children";
  var TITLE_SELECTOR = ".advance-free-gift-title";
  var STRIKE_SELECTOR = ".dyn-advance-free-gift-widget-list-template-product-strike-price";
  var CURRENT_SELECTOR = ".dyn-advance-free-gift-widget-list-template-product-current-price";
  var PRODUCT_ALT = "six-string-capo";
  var PRODUCT_TITLE = "Black Mountain Capo";
  // Cart "Best Sellers List" widget 2418 (list template one): full-price Capo
  // card. Only its current-price leaf is rewritten, and only when the card
  // shows no strike (discount) price.
  var LIST_ROOT_SELECTOR = ".dyn-list-widget-container.dyn-list-widget-id-2418";
  var LIST_CARD_SELECTOR = ".dyn-list-widget-card-parent";
  var LIST_TITLE_SELECTOR = ".dyn-list-widget-product-title";
  var LIST_STRIKE_SELECTOR = ".dyn-list-widget-product-strike-price";
  var LIST_CURRENT_SELECTOR = ".dyn-list-widget-product-current-price";

  var originals = new WeakMap();
  var lastWritten = new WeakMap();
  var touched = new Set();
  var observer = null;
  var pending = null;

  function bridgePrices() {
    var bridge = window.__bmDynamaticShopliftBridge;
    if (!bridge || typeof bridge.getOfferPrices !== "function") return null;
    var prices = bridge.getOfferPrices();
    if (!prices || typeof prices !== "object") return null;
    if (!Number.isSafeInteger(prices.baseCents) || prices.baseCents <= 0) return null;
    if (!Number.isSafeInteger(prices.discountedCents) || prices.discountedCents < 0) return null;
    if (prices.currency !== "USD") return null;
    return prices;
  }

  function bridgeListPrices() {
    var bridge = window.__bmDynamaticShopliftBridge;
    if (!bridge || typeof bridge.getListPrices !== "function") return null;
    var prices = bridge.getListPrices();
    if (!prices || typeof prices !== "object") return null;
    if (!Number.isSafeInteger(prices.baseCents) || prices.baseCents <= 0) return null;
    if (prices.currency !== "USD") return null;
    return prices;
  }

  function formatUSD(cents) {
    return "$" + (cents / 100).toFixed(2);
  }

  function isLeaf(node) {
    return !!node && !!node.children && node.children.length === 0;
  }

  function leafNodes(card, selector) {
    var matches = card.querySelectorAll(selector);
    var leaves = [];
    for (var i = 0; i < matches.length; i++) {
      if (isLeaf(matches[i])) leaves.push(matches[i]);
    }
    return leaves;
  }

  function cardMatches(card) {
    var images = card.querySelectorAll("img");
    var hasImage = false;
    for (var i = 0; i < images.length; i++) {
      if (images[i].alt === PRODUCT_ALT) { hasImage = true; break; }
    }
    if (!hasImage) return false;
    var title = card.querySelector(TITLE_SELECTOR);
    if (!title) return false;
    return title.textContent.trim() === PRODUCT_TITLE;
  }

  function listCardMatches(card) {
    if (card.getAttribute("handle") !== PRODUCT_ALT) return false;
    var titles = card.querySelectorAll(LIST_TITLE_SELECTOR);
    if (titles.length !== 1) return false;
    return titles[0].textContent.trim() === PRODUCT_TITLE;
  }

  function writePrice(node, value) {
    // Never touch a node with element children; write only when different so
    // our own mutation never re-triggers a write.
    if (!isLeaf(node)) return;
    if (node.textContent === value) return;
    if (!originals.has(node)) originals.set(node, node.textContent);
    node.textContent = value;
    lastWritten.set(node, value);
    touched.add(node);
  }

  function refreshList() {
    try {
      var roots = document.querySelectorAll(LIST_ROOT_SELECTOR);
      if (!roots.length) return;
      var prices = bridgeListPrices();
      if (!prices) return;
      var text = formatUSD(prices.baseCents);
      for (var r = 0; r < roots.length; r++) {
        var cards = roots[r].querySelectorAll(LIST_CARD_SELECTOR);
        for (var i = 0; i < cards.length; i++) {
          var card = cards[i];
          if (!listCardMatches(card)) continue;
          if (card.querySelector(LIST_STRIKE_SELECTOR)) continue;
          var currents = leafNodes(card, LIST_CURRENT_SELECTOR);
          if (currents.length !== 1) continue;
          writePrice(currents[0], text);
        }
      }
    } catch (error) {
      // Fail closed, as for the offer card.
    }
  }

  function refresh() {
    refreshList();
    try {
      var root = document.querySelector(ROOT_SELECTOR);
      if (!root) return;
      var prices = bridgePrices();
      if (!prices) return;
      var baseText = formatUSD(prices.baseCents);
      var discountedText = formatUSD(prices.discountedCents);
      var cards = root.querySelectorAll(CARD_SELECTOR);
      for (var i = 0; i < cards.length; i++) {
        var card = cards[i];
        if (!cardMatches(card)) continue;
        var strikes = leafNodes(card, STRIKE_SELECTOR);
        var currents = leafNodes(card, CURRENT_SELECTOR);
        if (strikes.length !== 1 || currents.length !== 1) continue;
        writePrice(strikes[0], baseText);
        writePrice(currents[0], discountedText);
      }
    } catch (error) {
      // Fail closed: any missing or changed app DOM leaves display untouched.
    }
  }

  function schedule() {
    if (pending !== null) return;
    pending = setTimeout(function () {
      pending = null;
      refresh();
    }, 0);
  }

  function onReady() {
    refresh();
  }

  function onForceOpen() {
    schedule();
  }

  function restore() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (pending !== null) {
      clearTimeout(pending);
      pending = null;
    }
    touched.forEach(function (node) {
      var original = originals.get(node);
      if (original === undefined) return;
      // Only revert when the node still holds our last written string; never
      // overwrite a later app change.
      if (node.textContent !== lastWritten.get(node)) return;
      node.textContent = original;
    });
    touched = new Set();
    originals = new WeakMap();
    lastWritten = new WeakMap();
    document.removeEventListener("DOMContentLoaded", onReady);
    window.removeEventListener("forceOpenDynCart", onForceOpen);
    if (window.__bmDynamaticShopliftOfferPrice === api) {
      delete window.__bmDynamaticShopliftOfferPrice;
      return true;
    }
    return false;
  }

  var api = {
    installed: true,
    refresh: refresh,
    restore: restore,
  };
  window.__bmDynamaticShopliftOfferPrice = api;

  if (typeof MutationObserver === "function") {
    observer = new MutationObserver(schedule);
    observer.observe(document, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onReady, { once: true });
  } else {
    refresh();
  }
  window.addEventListener("forceOpenDynCart", onForceOpen);

  refresh();
})();
